import { useCallback } from 'react';
import { useStore } from 'react-redux';
import checkrApiClient from '../../../../api/checkrApiClient';
import { CHECKR_BFF_API_BASE } from '../../../../constants';
import { verificationAxiosConfig } from '../../../../utils/verificationCredentials';
import {
  ExtendedFile,
  FailureReason,
  OcrPayload,
  OcrResponse,
  OcrResult,
  OcrResultsResponse,
} from './interfaces';

const OCR_ENDPOINT = `${CHECKR_BFF_API_BASE}/employment_document_realtime_ocr`;

const buildOcrPayload = (
  file: ExtendedFile,
  screeningId: string,
): OcrPayload => ({
  upload_id: file.uploadId,
  filename: file.filename,
  mimetype: file.mimetype,
  upload_url: file.url,
  employment_history_id: screeningId,
});

interface OcrAccess {
  verificationId: string;
  config: { headers: Record<string, string>; withCredentials: boolean };
}

const postEmploymentDocumentOcr = (
  payload: OcrPayload,
  access: OcrAccess | null,
) =>
  access
    ? checkrApiClient.post<OcrResponse>(
        OCR_ENDPOINT,
        { ...payload, verification_id: access.verificationId },
        access.config,
      )
    : checkrApiClient.post<OcrResponse>(OCR_ENDPOINT, payload);

const getEmploymentDocumentOcr = (uploadId: string, access: OcrAccess | null) =>
  access
    ? checkrApiClient.get<OcrResultsResponse>(`${OCR_ENDPOINT}/${uploadId}`, {
        ...access.config,
        params: { verification_id: access.verificationId },
      })
    : checkrApiClient.get<OcrResultsResponse>(`${OCR_ENDPOINT}/${uploadId}`);

const useOcrAccess = () => {
  const store = useStore();
  return useCallback(
    async (method: string): Promise<OcrAccess | null> => {
      const state = store.getState() as any;
      const verificationId: string | undefined =
        state.verifications?.verification?.id;
      const config = await verificationAxiosConfig(
        verificationId,
        state,
        method,
      );
      return config && verificationId ? { verificationId, config } : null;
    },
    [store],
  );
};

export const useEmploymentDocumentOcrResult = () => {
  const ocrAccess = useOcrAccess();
  const getOcrResult = useCallback(
    async (uploadId: string, filename: string): Promise<OcrResult> => {
      try {
        const { data } = await getEmploymentDocumentOcr(
          uploadId,
          await ocrAccess('GET'),
        );
        return { ...data, filename, upload_id: uploadId };
      } catch {
        return null;
      }
    },
    [ocrAccess],
  );

  return { getOcrResult };
};

export interface OcrRequestOutcome {
  success: boolean;
  failureReason: FailureReason;
}

// A POST that fails up front never starts polling, so its reason has to come
// back from here rather than from a polled terminal. The BFF's own
// failure_reason wins; a request that never reached it (null response), or a
// failure it didn't explain, is a processing failure the candidate can retry.
const toOcrRequestOutcome = (response: OcrResponse | null): OcrRequestOutcome =>
  response && response.status !== 'failed'
    ? { success: true, failureReason: null }
    : {
        success: false,
        failureReason: response?.failure_reason ?? 'failed_to_process',
      };

interface UseEmploymentDocumentOcrReturn {
  sendOcrRequest: (payload: OcrPayload) => Promise<OcrResponse | null>;
  sendOcrRequestsForFile: (
    file: ExtendedFile,
    screeningId: string,
  ) => Promise<OcrRequestOutcome>;
}

export const useEmploymentDocumentOcr = (): UseEmploymentDocumentOcrReturn => {
  const ocrAccess = useOcrAccess();
  const sendOcrRequest = useCallback(
    async (payload: OcrPayload): Promise<OcrResponse | null> => {
      return postEmploymentDocumentOcr(payload, await ocrAccess('POST'))
        .then(({ data }) => data)
        .catch(() => null);
    },
    [ocrAccess],
  );

  const sendOcrRequestsForFile = useCallback(
    async (
      file: ExtendedFile,
      screeningId: string,
    ): Promise<OcrRequestOutcome> => {
      const payload = buildOcrPayload(file, screeningId);
      const response = await sendOcrRequest(payload);

      return toOcrRequestOutcome(response);
    },
    [sendOcrRequest],
  );

  return { sendOcrRequest, sendOcrRequestsForFile };
};
